import React, { useEffect, useState } from 'react'
import { Dialog, DialogContent, makeStyles } from '@material-ui/core'
import { useTranslate } from 'react-admin'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import YouTubeIcon from '@material-ui/icons/YouTube'
import Videocam from '@material-ui/icons/Videocam'
import calendarEventClickModalStyles from './calendarEventClickModal.styles'
import { useTeamCalendarContext } from '../../../context/useTeamCalendarContext'
import BoxEventTypeColor from '../../../components/BoxEventTypeColor/BoxEventTypeColor'
import MyDeleteIcon from '../../../assets/icons/MyDeleteIcon'
import MyEditIcon from '../../../assets/icons/MyEditIcon'
import MyCloseIcon from '../../../assets/icons/MyCloseIcon'
import CalendarIcon from '../../../assets/icons/calendarIcon'
import DownloadIcon from '../../../assets/icons/downloadIcon'
import MyGroupIcon from '../../../assets/icons/myGroupIcon'
import useHistoryPush from '../../../hooks/useHistoryPush'
import useQueryParams from '../../../hooks/useQueryParams'
import BROADCAST_STATES from '../../../utils/constants/broadcastStates'
import MyCopyIcon from '../../../assets/icons/MyCopyIcon'
import QueryKeys from '../../../utils/constants/queryKeys'
import BarChart from '@material-ui/icons/BarChart'
import getDownloadRecordingUrl from '../../../Apis/event/getDownloadRecordingUrl'
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender'
import IsUserAddonsAuthorized from '../../../components/IsUserAddonsAuthorized/IsUserAddonsAuthorized'
import { USER_ADDONS } from '../../../utils/constants/userAddons'
import isUserAddonsValid from '../../../utils/helpers/isUserAddonsValid'
import startLivestream from '../../../Apis/livestream/startLivestream'
import ButtonWithLoader from '../../../components/ui/ButtonWithLoader/ButtonWithLoader'
import COLORS from '../../../utils/constants/colors'
import GetGameSummary from '../../../Apis/gameSummary/getGameSummary'
import getStatsGameSetup from '../../../Apis/stats/getStatsGameSetup'
import downloadOverlayLinks from '../../../Apis/stats/downloadOverlayLinks'
import STATS_GAME_STATUS from '../../../utils/constants/statsGameStatus'
import LiteModeBadge from '../../../components/LiteModeBadge/LiteModeBadge'
import USER_PERMISSIONS from '../../../utils/constants/userPermissions'
import IsPermitted from '../../../components/IsPermitted/IsPermitted'
import loadingDots from '../../../assets/images/loading-dots.svg'

// readOnly (the calendar on a phone) keeps the details, the YouTube link and the downloads, and
// drops everything that changes the event or opens a page a phone doesn't get.
// paired ({ orgId, competitionId }) is a federation game a paired club reads: its details and
// a link to its stats under the federation's routes, nothing that acts on it.
const CalendarEventClickModal = ({ open, handleClose, onEdit, onDelete, readOnly, paired }) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const [isError, setIsError] = useState(false)
  const [copyRecordingClicked, setCopyRecordingClicked] = useState(false)
  const [showCopyAlert, setShowCopyAlert] = useState(false)
  const [preparationAlert, setPreparationAlert] = useState(false)
  const [isRecordingPreparing, setIsRecordingPreparing] = useState(false)
  const queryClient = useQueryClient()
  const {
    setSelectedEventType,
    event,
    eventIsPlaceholder,
    deleteEvent,
    clickedEventId,
    setClickedEventId,
    coordinatesModal,
  } = useTeamCalendarContext()
  const {
    notes,
    hasRecording,
    isRecordingReady,
    type,
    title,
    teams,
    startDateTime,
    broadcast,
    ytBroadcastDetails,
  } = event || {}
  const { broadcastUrl } = ytBroadcastDetails || {}
  // The calendar's org: the recording and overlay-link actions only show for its own events (never paired).
  const { org: orgId } = useQueryParams()
  const translate = useTranslate()
  const { redirect, pathname } = useHistoryPush()
  const useStyles = makeStyles(calendarEventClickModalStyles(coordinatesModal, readOnly))
  const classes = useStyles()
  const isDeleteIconDisabled = broadcast?.state && broadcast?.state !== BROADCAST_STATES.scheduled
  const allowWatchVideo =
    !readOnly &&
    !paired &&
    broadcast?.id &&
    ((broadcast?.state === BROADCAST_STATES.finished && hasRecording) ||
      broadcast?.state === BROADCAST_STATES.running)
  const allowStartLive = !readOnly && !paired && broadcast?.state === BROADCAST_STATES.scheduled
  const [isStartLiveDisabled, setIsStartLiveDisabled] = useState(false)
  const [isConfirmingStartLive, setIsConfirmingStartLive] = useState(false)

  useEffect(() => {
    if (deleteEvent.isSuccess) {
      setIsConfirmingDelete(false)
      setIsStartLiveDisabled(false)
      handleClose()
      setClickedEventId('')
    }
  }, [deleteEvent.isSuccess])

  useEffect(() => {
    setIsError(false)
  }, [])

  useEffect(() => {
    let timeId = null
    let prepTimeId = null

    if (showCopyAlert) {
      timeId = setTimeout(() => {
        setShowCopyAlert(false)
        setCopyRecordingClicked(false)
      }, 5000)
    }
    if (preparationAlert) {
      prepTimeId = setTimeout(() => {
        setPreparationAlert(false)
      }, 5000)
    }

    return () => {
      if (timeId) {
        clearTimeout(timeId)
      }
      if (prepTimeId) {
        clearTimeout(prepTimeId)
      }
    }
  }, [showCopyAlert, preparationAlert])

  const getRecMutation = useMutation(() => getDownloadRecordingUrl({ orgId, streamId: broadcast?.id }), {
    onSuccess: (res) => {
      if (res?.link && !res?.preparing) {
        window.location.replace(res?.link)
      } else if (res?.preparing) {
        setIsRecordingPreparing(true)
        setPreparationAlert(true)
      }
    },
  })

  // Safari requires clipboard writes to stay inside the user gesture, so we hand
  // it a ClipboardItem whose value is the pending fetch instead of awaiting the
  // network request first (which would drop the gesture and throw NotAllowedError).
  const handleCopyRecording = () => {
    const item = new ClipboardItem({
      'text/plain': getDownloadRecordingUrl({ orgId, streamId: broadcast?.id }).then(
        (data) => new Blob([data?.link ?? ''], { type: 'text/plain' }),
      ),
    })
    navigator.clipboard
      .write([item])
      .then(() => setCopyRecordingClicked(true))
      .catch(() => console.error('Failed to write to clipboard:'))
  }

  const { data: gameSetup, status, isLoading: isGameSetupLoading } = useQuery(
    [QueryKeys.GET_STATS_GAME_SETUP, event?.id],
    () => getStatsGameSetup({ gameId: event?.id, skipErrorNotification: true }),
    {
      enabled: Boolean(event?.id) && !paired && isUserAddonsValid([USER_ADDONS.STATS]),
      retry: (failureCount, error) => {
        if (error?.response?.status === 400 || error?.response?.status === 404) {
          return false
        }
        return failureCount < 2
      },
    },
  )

  const { refetch: fetchGameSummary, error } = useQuery(
    [QueryKeys.GET_GAME_SUMMARY],
    async () => {
      const response = await GetGameSummary({ orgId, gameId: event?.id })
      const formattedDate = event?.startDateTime
        ? new Date(event?.startDateTime).toISOString().split('T')[0]
        : 'unknown_date'

      const blob = new Blob([response], { type: 'text/plain' })

      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = `${event?.title}_${formattedDate}.txt`
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    {
      enabled: false,
      retry: (failureCount, error) => {
        if (error?.response?.status === 400 || error?.response?.status === 404) {
          setIsError(true)
          return false
        }
        return failureCount < 2
      },
    },
  )

  // The modal opens on the calendar list's entry; the links hang on fields only the event's detail
  // brings (hasStats, pairedGame, hasRecording) and, for a game, on its stats setup.
  const areLinksLoading = eventIsPlaceholder || (type?.code === 'game' && isGameSetupLoading)

  const downloadOverlayLinksMutation = useMutation(() =>
    downloadOverlayLinks({ orgId, gameId: event?.id }),
  )

  const startLivestreamMutation = useMutation((broadcastId) => startLivestream(broadcastId, orgId), {
    onSuccess: () => {
      setIsStartLiveDisabled(true)
      setIsConfirmingStartLive(false)
    },
  })

  const handleEditClick = () => {
    setSelectedEventType(event.type)
    setIsStartLiveDisabled(false)
    handleClose()
    // On the CMS page there's no `/cms/:id` route, so the redirect below dead-ends in a
    // "Page not found". When an onEdit handler is supplied (CMS), open the edit drawer in
    // place instead — the same flow the game row's pencil already uses.
    if (onEdit) {
      onEdit(event.id)
      return
    }
    redirect(`${pathname}/${event.id}`, [{ eventType: event?.type?.code }])
  }

  const handleDelete = () =>
    deleteEvent.mutate(
      {
        eventTypeCode: event?.type?.code,
        eventId: clickedEventId,
      },
      // The context mutation only invalidates the calendar queries (GET_EVENTS/GET_STAGES).
      // On the CMS page the game cards are sourced from a different query, so let the caller
      // hook in its own refetch on success — mirrors how the edit button defers to onEdit.
      onDelete ? { onSuccess: onDelete } : undefined,
    )

  const handleCloseEventClickModal = () => {
    setIsStartLiveDisabled(false)
    setIsConfirmingStartLive(false)
    setIsConfirmingDelete(false)
    setIsError(false)
    setClickedEventId('')
    setCopyRecordingClicked(false)
    setPreparationAlert(false)
    setIsRecordingPreparing(false)
    queryClient.removeQueries([QueryKeys.GET_STATS_GAME_SETUP])
    handleClose()
  }

  const handleDeleteIcon = () => setIsConfirmingDelete(true)

  const handleDownloadIcon = () => {
    getRecMutation.mutate()
  }

  const handleCopyIcon = (textToCopy) => {
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setShowCopyAlert(true)
      })
      .catch(() => console.error('Failed to write to clipboard:'))
  }

  const handleWatchVideoClick = (eventId, broadcastId) =>
    redirect(`/livestream/${broadcastId}`, [{ eventId }])

  const handleGameStatsClick = (eventId) => {
    if (paired) {
      redirect(`/game/stats`, [{ eventId }, { fedOrg: paired.orgId }, { competition: paired.competitionId }])
      return
    }
    redirect(`/game/stats`, [{ eventId }])
  }

  const handleStartVideoClick = (broadcastId) => startLivestreamMutation.mutate(broadcastId)

  return (
    <Dialog
      id='calendar-event-dialog'
      open={open}
      onClose={handleCloseEventClickModal}
      fullWidth
      BackdropProps={{ style: readOnly ? undefined : { backgroundColor: 'transparent' } }}
      classes={{ paper: `${classes.dialog} ${isConfirmingDelete ? classes.dialogDanger : ''}` }}
      disableScrollLock
    >
      <DialogContent>
        <div className={classes.dialogHeader}>
          <div className={classes.headerTitleContainer}>
            <BoxEventTypeColor color={type?.color} xs={1} />
            <div className={classes.headerTitle}>{title}</div>
            {event?.hasStats && <LiteModeBadge statsMode={event?.statsMode} />}
          </div>
          <div className={classes.headerActions}>
            <ConditionalRender renderValue={!isConfirmingDelete && !readOnly && !paired}>
              <IsPermitted permission={USER_PERMISSIONS.UPDATE_EVENT}>
                <MyEditIcon onClick={handleEditClick} className={classes.orangeFill} />
                <MyDeleteIcon
                  className={isDeleteIconDisabled ? classes.deleteIconDisabled : classes.orangeFill}
                  disabled={isDeleteIconDisabled}
                  onClick={handleDeleteIcon}
                />
              </IsPermitted>
            </ConditionalRender>
            <MyCloseIcon className={classes.orangeFill} onClick={handleCloseEventClickModal} />
          </div>
        </div>

        <div className={isConfirmingDelete ? classes.dimmed : undefined}>
          <div className={classes.flexDiv}>
            <CalendarIcon xs={1} />
            <span>
              {startDateTime &&
                `${new Date(startDateTime).toLocaleDateString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'long',
                })} ${new Date(startDateTime).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`}
            </span>
          </div>

          <div className={classes.flexDiv}>
            <MyGroupIcon xs={1} />
            <span>{teams?.map((team) => team.name).join(', ')}</span>
          </div>

          <ConditionalRender renderValue={notes}>
            <div className={classes.notes}>{notes}</div>
          </ConditionalRender>

          <div className={classes.divider} />

          <ConditionalRender renderValue={Boolean(paired && type?.code === 'game' && event?.hasStats && !eventIsPlaceholder)}>
            <div className={classes.actionRow} onClick={() => handleGameStatsClick(clickedEventId)}>
              <BarChart className={classes.actionIcon} />
              <span>View Stats</span>
            </div>
          </ConditionalRender>

          <ConditionalRender renderValue={!paired}>
            <ConditionalRender renderValue={allowWatchVideo}>
              <div
                className={classes.actionRow}
                onClick={() => handleWatchVideoClick(clickedEventId, broadcast?.id)}
              >
                <Videocam className={classes.actionIcon} />
                <span>{translate('ra.buttons.watchVideo')}</span>
              </div>
            </ConditionalRender>
            <ConditionalRender renderValue={allowStartLive}>
              {isConfirmingStartLive ? (
                <div className={classes.inlineConfirm}>
                  <span className={classes.inlineConfirmText}>
                    {translate('ra.text.startLivestream')}
                  </span>
                  <div className={classes.inlineConfirmActions}>
                    <button
                      type='button'
                      className={classes.inlineCancelButton}
                      onClick={() => setIsConfirmingStartLive(false)}
                    >
                      {translate('ra.buttons.cancel')}
                    </button>
                    <ButtonWithLoader
                      style={{ backgroundColor: COLORS.red }}
                      type='button'
                      label={translate('ra.buttons.startVideo')}
                      loading={startLivestreamMutation.isLoading}
                      onClick={() => handleStartVideoClick(broadcast?.id)}
                    />
                  </div>
                </div>
              ) : (
                <div
                  className={classes.actionRow}
                  onClick={() => !isStartLiveDisabled && setIsConfirmingStartLive(true)}
                  style={isStartLiveDisabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                >
                  <Videocam className={classes.actionIcon} />
                  <span>{translate('ra.buttons.startVideo')}</span>
                </div>
              )}
            </ConditionalRender>

            <IsUserAddonsAuthorized acceptedAddons={[USER_ADDONS.STATS]}>
              <ConditionalRender
                renderValue={
                  !readOnly && type?.code === 'game' && !!gameSetup && status === 'success'
                }
              >
                <div
                  className={classes.actionRow}
                  onClick={() => handleGameStatsClick(clickedEventId)}
                >
                  <BarChart className={classes.actionIcon} />
                  <span>View Stats</span>
                </div>
              </ConditionalRender>
            </IsUserAddonsAuthorized>

            <IsUserAddonsAuthorized acceptedAddons={[USER_ADDONS.STATS]}>
              <ConditionalRender renderValue={gameSetup?.status === STATS_GAME_STATUS.COMPLETED}>
                <ConditionalRender renderValue={!isError || error?.response?.status !== 404}>
                  <div
                    className={classes.actionRow}
                    onClick={fetchGameSummary}
                    style={preparationAlert ? { display: 'none' } : undefined}
                  >
                    <DownloadIcon className={classes.actionIcon} style={{ fill: 'currentColor' }} />
                    <span>{translate('ra.text.downloadSummary')}</span>
                  </div>
                </ConditionalRender>
                <ConditionalRender renderValue={isError && error?.response?.status === 404}>
                  <div className={classes.flexDiv}>
                    <span className={classes.gameSummaryAlert}>
                      {translate('ra.text.gameSummaryNotification')}
                    </span>
                  </div>
                </ConditionalRender>
              </ConditionalRender>
            </IsUserAddonsAuthorized>

            <IsUserAddonsAuthorized acceptedAddons={[USER_ADDONS.LIVESTREAM]}>
              <ConditionalRender renderValue={hasRecording}>
                <div
                  className={classes.actionRow}
                  onClick={handleDownloadIcon}
                  style={preparationAlert ? { display: 'none' } : undefined}
                >
                  <DownloadIcon className={classes.actionIcon} style={{ fill: 'currentColor' }} />
                  <span>{translate('ra.text.downloadRecording')}</span>
                  <MyCopyIcon
                    style={{ display: isRecordingReady ? 'inline' : 'none' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleCopyRecording()
                    }}
                  />
                  <ConditionalRender renderValue={copyRecordingClicked}>
                    <span className={classes.copyAlert}>{translate('ra.text.copied')}</span>
                  </ConditionalRender>
                </div>
              </ConditionalRender>
              <ConditionalRender renderValue={preparationAlert && isRecordingPreparing}>
                <div className={classes.flexDiv}>
                  <span className={classes.copyAlert}>{translate('ra.text.emailNotification')}</span>
                </div>
              </ConditionalRender>
              <ConditionalRender renderValue={broadcastUrl}>
                <div className={classes.flexDiv}>
                  <YouTubeIcon />
                  <a href={broadcastUrl} target='_blank' rel='noopener noreferrer'>
                    {broadcastUrl}
                  </a>
                  <MyCopyIcon onClick={() => handleCopyIcon(broadcastUrl)} />
                  <ConditionalRender renderValue={showCopyAlert && !copyRecordingClicked}>
                    <span className={classes.copyAlert}>{translate('ra.text.copied')}</span>
                  </ConditionalRender>
                </div>
              </ConditionalRender>
            </IsUserAddonsAuthorized>

            {/* A club game has stats behind it through its connected federation game. */}
            <IsPermitted permission={USER_PERMISSIONS.DOWNLOAD_OVERLAY_LINKS}>
              <ConditionalRender
                renderValue={
                  !readOnly &&
                  type?.code === 'game' &&
                  (event?.hasStats || Boolean(event?.pairedGame)) &&
                  !isGameSetupLoading &&
                  gameSetup?.status !== STATS_GAME_STATUS.COMPLETED &&
                  gameSetup?.status !== STATS_GAME_STATUS.FORFEIT
                }
              >
                <div
                  className={classes.actionRow}
                  onClick={() => downloadOverlayLinksMutation.mutate()}
                >
                  <DownloadIcon className={classes.actionIcon} style={{ fill: 'currentColor' }} />
                  <span>{translate('ra.text.overlayLinks')}</span>
                </div>
              </ConditionalRender>
            </IsPermitted>
          </ConditionalRender>

          <ConditionalRender renderValue={areLinksLoading}>
            <img alt='' src={loadingDots} className={classes.linksLoading} />
          </ConditionalRender>
        </div>
      </DialogContent>

      <ConditionalRender renderValue={isConfirmingDelete}>
        <div className={classes.dangerBar}>
          <span className={classes.dangerBarText}>{translate('ra.text.deleteEventPrompt')}</span>
          <div className={classes.dangerBarActions}>
            <button
              type='button'
              className={classes.keepButton}
              onClick={() => setIsConfirmingDelete(false)}
            >
              {translate('ra.buttons.keep')}
            </button>
            <ButtonWithLoader
              type='button'
              className={classes.deleteConfirmButton}
              label={translate('ra.buttons.delete')}
              loading={deleteEvent.isLoading}
              onClick={handleDelete}
            />
          </div>
        </div>
      </ConditionalRender>
    </Dialog>
  )
}

CalendarEventClickModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  readOnly: PropTypes.bool,
  paired: PropTypes.shape({ orgId: PropTypes.string, competitionId: PropTypes.string }),
}

export default CalendarEventClickModal
